import _ from 'lodash'
import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useParams } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'

import { PatientDocumentList, type PatientDocumentListProps } from '@/features/patient/patient-documents'
import { documentOfflineStore, OfflineDocument } from '@/core/offline'
import { ObjectType } from '@/core/models/document.model'
import { useSyncContext } from '@/core/offline/contexts/SyncContext'

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix to get just the base64 data
      resolve(result.split(',')[1])
    }
    reader.onerror = (error) => reject(error)
  })
}

export type OfflinePatientDocumentListProps = {
  patientId: string
}

export function OfflineDocuments({ patientId }: OfflinePatientDocumentListProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [documents, setDocuments] = useState<OfflineDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [documentUrls, setDocumentUrls] = useState<Map<string, string>>(new Map())
  const { addToSyncQueue } = useSyncContext()

  // Load documents for this patient
  const loadDocuments = useCallback(async () => {
    if (!patientId) return

    try {
      setIsLoading(true)
      console.log('Loading documents for patient:', patientId)

      const patientDocuments = await documentOfflineStore.getByObjectId('patient', patientId)
      console.log(
        'Found patient documents:',
        patientDocuments.length,
        patientDocuments.map((d) => ({
          filename: d.filename,
          objectType: d.objectType,
          objectId: d.objectId,
          url: d.url,
        }))
      )

      setDocuments(patientDocuments)

      // Generate blob URLs for offline documents
      const urlMap = new Map<string, string>()
      for (const doc of patientDocuments) {
        if (!doc.url && doc._fileData) {
          try {
            console.log('Generating blob URL for document:', doc.filename)
            const blobUrl = await documentOfflineStore.getFileUrl(doc)
            if (blobUrl) {
              urlMap.set(doc._id, blobUrl)
              console.log('Generated blob URL for:', doc.filename, blobUrl.substring(0, 50) + '...')
            } else {
              console.warn('No blob URL generated for:', doc.filename)
            }
          } catch (error) {
            console.error('Failed to generate blob URL for document:', doc.filename, error)
          }
        } else {
          console.log('Document has URL or no file data:', doc.filename, { hasUrl: !!doc.url, hasFileData: !!doc._fileData })
        }
      }
      console.log('Document URLs generated:', urlMap.size)
      setDocumentUrls(urlMap)
    } catch (error) {
      console.error('Failed to load offline documents:', error)
      enqueueSnackbar(t('notification.document.load.error'), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }, [patientId])

  useEffect(() => {
    loadDocuments()
  }, [patientId])

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      documentUrls.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
    }
  }, [documentUrls])

  // Convert offline documents to the format expected by the Documents component
  const documentsForComponent = useMemo(
    () =>
      documents.map((doc) => {
        console.log('Processing document for display:', {
          filename: doc.filename,
          key: doc.key,
          url: doc.url,
          blobUrl: documentUrls.get(doc._id),
          objectType: doc.objectType,
          objectId: doc.objectId,
        })

        return {
          filename: doc.filename,
          key: doc.key,
          url: doc.url || documentUrls.get(doc._id) || '', // Use generated blob URL for offline documents
          isOffline: !doc.url, // Flag to indicate this is an offline document
        }
      }),
    [documents, documentUrls]
  )

  const handleDelete = async (deletedDocuments: PatientDocumentListProps['documents']) => {
    if (!deletedDocuments.length) return

    try {
      setIsDeleting(true)

      // Delete documents from offline store
      for (const document of deletedDocuments) {
        await documentOfflineStore.deleteByKey(document.key)
      }

      // Reload documents
      await loadDocuments()

      enqueueSnackbar(t('notification.document.delete.success'), { variant: 'success' })
    } catch (error) {
      console.error('Failed to delete offline documents:', error)
      enqueueSnackbar(t('notification.document.delete.error'), { variant: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSaveFilename = async (newFilename: string, key: string) => {
    try {
      await documentOfflineStore.updateFilename(key, newFilename)
      await loadDocuments()
      enqueueSnackbar(t('notification.document.updated.success'), { variant: 'success' })
    } catch (error) {
      console.error('Failed to update filename:', error)
      enqueueSnackbar(t('notification.document.updated.error'), { variant: 'error' })
    }
  }

  const handleSaveFileAnnotation = async (file: File, key: string) => {
    // For now, we'll handle this as a new file upload
    // In a more advanced implementation, we could update the existing document
    await handleUpload(file)
  }

  const handleUpload = async (documentFile: File) => {
    if (!patientId) return

    try {
      setIsLoading(true)

      // Generate a unique key for the document
      const fileKey = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Convert file to base64
      const base64Data = await fileToBase64(documentFile)

      // Store the document offline with correct parameters
      const createdDocument = await documentOfflineStore.createOfflineDocument({
        filename: documentFile.name,
        size: documentFile.size,
        mimeType: documentFile.type,
        base64Data: base64Data,
        consultationId: patientId, // This will be used as objectId in the store
        objectType: 'patient', // Explicitly set object type to patient
      })

      await addToSyncQueue({
        entityType: 'document',
        entityId: createdDocument._offlineId || createdDocument._id,
        operation: 'create',
        priority: 0,
        dependencies: [],
        payload: createdDocument,
        data: createdDocument,
        timestamp: createdDocument._createdAt,
        maxRetries: 5,
      })

      // Reload documents
      await loadDocuments()

      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
    } catch (error) {
      console.error('Failed to upload document offline:', error)
      enqueueSnackbar(t('notification.document.create.error'), { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!patientId) return

      for (const file of acceptedFiles) {
        try {
          console.log('Processing dropped file:', file.name)
          const base64Data = await fileToBase64(file)

          const createdDocument = await documentOfflineStore.createOfflineDocument({
            filename: file.name,
            size: file.size,
            mimeType: file.type,
            base64Data,
            consultationId: patientId, // This will be used as objectId in the store
            objectType: 'patient', // Explicitly set object type to patient
          })

          console.log('Created patient document:', {
            filename: createdDocument.filename,
            objectType: createdDocument.objectType,
            objectId: createdDocument.objectId,
          })

          await addToSyncQueue({
            entityType: 'document',
            entityId: createdDocument._offlineId || createdDocument._id,
            operation: 'create',
            priority: 0,
            dependencies: [],
            payload: createdDocument,
            data: createdDocument,
            timestamp: createdDocument._createdAt,
            maxRetries: 5,
          })

          console.log('Added document to sync queue')
          enqueueSnackbar(t('notifications.success.fileUploaded'), { variant: 'success' })
        } catch (error) {
          console.error('Failed to upload file:', error)
          enqueueSnackbar(t('notifications.error.fileUploadFailed'), { variant: 'error' })
        }
      }
      loadDocuments()
    },
    [patientId, enqueueSnackbar, t, loadDocuments, addToSyncQueue]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  // Temporary test function - remove after debugging
  const testPatientDocumentCreation = async () => {
    if (!patientId) return

    try {
      console.log('🧪 Testing patient document creation...')

      const testDocument = await documentOfflineStore.createOfflineDocument({
        filename: 'test-document.pdf',
        size: 1024,
        mimeType: 'application/pdf',
        base64Data: 'dGVzdCBkYXRh', // "test data" in base64
        consultationId: patientId,
        objectType: 'patient',
      })

      console.log('✅ Test document created:', {
        id: testDocument._id,
        filename: testDocument.filename,
        objectType: testDocument.objectType,
        objectId: testDocument.objectId,
      })

      // Reload to see if it appears
      await loadDocuments()
    } catch (error) {
      console.error('❌ Test document creation failed:', error)
    }
  }

  // Uncomment to test: testPatientDocumentCreation()

  return (
    <PatientDocumentList
      documents={documentsForComponent}
      totalCount={documentsForComponent.length}
      isDeleting={isDeleting}
      onDelete={handleDelete}
      onSaveFileName={handleSaveFilename}
      onSaveFileAnnotation={handleSaveFileAnnotation}
      onUpload={handleUpload}
      qrCode=""
    />
  )
}
